import React, { useEffect, useState } from 'react';
import { Box, Button, ButtonBase, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import BiReport from '../BusinessIntelligence/BiReport/BiReport';
import homeScreenService from '../../service/homeScreen.service';
import {
  getHomeScreenBackgroundImageSource,
  getHomeScreenImageSource,
  HomeScreenLinkTypes,
  HomeScreenTileTypes,
} from '../../config/homeScreen';
import addNotification from '../../utils/addNotification';
import { getIcon } from '../../utils/helper';
import { NotificationType } from '../../config';
import homeScreenBackground from '../../assets/homeScreen/home-screen-background.jpg';

const byVisOrder = (first, second) => Number(first.visOrder) - Number(second.visOrder);
const gridRowHeight = 180;
const getHomeScreenBackgroundStyle = (homeScreen) => ({
  backgroundImage: `linear-gradient(rgba(243, 244, 246, 0.28), rgba(243, 244, 246, 0.28)), url(${
    getHomeScreenBackgroundImageSource(homeScreen) || homeScreenBackground
  })`,
});

const HomeScreenTile = ({ tile, interactive = true }) => {
  const navigate = useNavigate();
  const height = Math.max(1, Number(tile.height) || 1);
  const width = Math.min(6, Math.max(1, Number(tile.width) || 1));
  const hasLink = interactive && Boolean(tile.linkType && tile.linkTarget);
  const hasHeader = Boolean(tile.title);
  const tilePixelHeight = height * gridRowHeight;
  const reportAvailableHeight = Math.max(1, tilePixelHeight - (hasHeader ? 42 : 0));
  const minimumReportHeight = 180;
  const reportScale = Math.min(1, reportAvailableHeight / minimumReportHeight);
  const reportRenderHeight = Math.max(minimumReportHeight, reportAvailableHeight);

  const openLink = () => {
    if (!hasLink) return;

    const linkTarget =
      tile.linkType === HomeScreenLinkTypes.INTERNAL && !/^\/app(?:\/|$)/i.test(tile.linkTarget)
        ? `/app${tile.linkTarget}`
        : tile.linkTarget;

    if (tile.openInNewTab) {
      window.open(linkTarget, '_blank', 'noopener,noreferrer');
      return;
    }

    if (tile.linkType === HomeScreenLinkTypes.EXTERNAL) {
      window.location.assign(linkTarget);
      return;
    }

    navigate(linkTarget);
  };

  return (
    <Paper
      component="article"
      elevation={0}
      sx={{
        gridColumn: {
          xs: `span ${Math.min(width, 2)}`,
          sm: `span ${Math.min(width, 4)}`,
          lg: `span ${width}`,
        },
        gridRow: `span ${height}`,
        minHeight: `${height * gridRowHeight}px`,
        minWidth: 0,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'grey.500',
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12), 0 12px 30px rgba(15, 23, 42, 0.08)',
        backdropFilter: 'blur(2px)',
        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        cursor: hasLink ? 'pointer' : 'default',
        ...(hasLink && {
          '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: 'rgba(10, 110, 209, 0.5)',
            boxShadow: '0 6px 16px rgba(15, 23, 42, 0.16), 0 18px 36px rgba(15, 23, 42, 0.10)',
          },
        }),
        '&:hover [data-home-screen-tile-image]': { transform: 'scale(1.015)' },
      }}
    >
      {hasHeader && (
        <Box
          component="header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 42,
            px: 2,
            borderBottom: '1px solid rgba(226, 232, 240, 0.7)',
            bgcolor: 'rgba(255, 255, 255, 0.8)',
          }}
        >
          <Typography noWrap sx={{ fontSize: '0.95rem', fontWeight: 600, letterSpacing: '-0.01em', color: '#1e293b' }}>
            {tile.title}
          </Typography>
        </Box>
      )}
      {tile.type === HomeScreenTileTypes.BI_REPORT && (
        <Box sx={{ overflow: 'hidden', bgcolor: '#f7f9fb', height: reportAvailableHeight }}>
          <Box
            sx={{
              transform: `scale(${reportScale})`,
              transformOrigin: 'top left',
              width: `${100 / reportScale}%`,
            }}
          >
            <BiReport
              reportId={tile.biReportId}
              height={reportRenderHeight}
              filter={{ interval: { from: null, to: null } }}
              dashboardData={{}}
              dashboardView
              hideControls
            />
          </Box>
        </Box>
      )}
      {tile.type === HomeScreenTileTypes.IMAGE && (
        <Box
          component="img"
          data-home-screen-tile-image=""
          sx={{ display: 'block', width: '100%', height: reportAvailableHeight, objectFit: 'cover', transition: 'transform 300ms ease' }}
          src={getHomeScreenImageSource(tile)}
          alt={tile.imageAltText ?? ''}
        />
      )}
      {tile.type === HomeScreenTileTypes.STATIC_TEXT && (
        <Box
          sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, height: reportAvailableHeight, overflow: 'auto', px: 2, py: 1.5, color: '#334155' }}
        >
          <Typography component="div" sx={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.625 }}>
            {tile.textContent}
          </Typography>
          {tile.icon && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {getIcon(tile.icon, '2.5rem', '#6baed6')}
            </Box>
          )}
        </Box>
      )}
      {hasLink && (
        <ButtonBase
          aria-label="Hivatkozás megnyitása"
          onClick={openLink}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            borderRadius: 'inherit',
            '&:focus-visible': {
              outline: '2px solid #0a6ed1',
              outlineOffset: '-2px',
            },
          }}
        />
      )}
    </Paper>
  );
};

const TileGrid = ({ tiles, interactive }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(2, minmax(0, 1fr))',
        sm: 'repeat(4, minmax(0, 1fr))',
        lg: 'repeat(6, minmax(0, 1fr))',
      },
      gridAutoRows: '180px',
      gap: 1.5,
    }}
  >
    {[...(tiles ?? [])].sort(byVisOrder).map((tile, index) => (
      <HomeScreenTile key={tile.id ?? tile._clientId ?? `${tile.type}-${index}`} tile={tile} interactive={interactive} />
    ))}
  </Box>
);

export const HomeScreenPreview = ({ homeScreen, interactive = false, showBackground = false }) => {
  const groups = [...(homeScreen?.tileGroups ?? [])].sort(byVisOrder);

  return (
    <Stack
      spacing={2.5}
      sx={{
        ...(showBackground && {
          minHeight: 'calc(100vh - 10rem)',
          p: 2,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          ...getHomeScreenBackgroundStyle(homeScreen),
        }),
      }}
    >
      {(homeScreen?.tiles?.length ?? 0) > 0 && <TileGrid tiles={homeScreen.tiles} interactive={interactive} />}
      {groups.map((group, index) => (
        <Box component="section" key={group.id ?? group._clientId ?? index} sx={{ py: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <Box sx={{ width: 4, height: 20, flexShrink: 0, borderRadius: 999, bgcolor: 'white', boxShadow: '0 1px 3px rgba(15, 23, 42, 0.45)' }} />
            <Typography component="h2" sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', color: 'white', textShadow: '0 1px 3px rgba(15, 23, 42, 0.85)' }}>
              {group.name}
            </Typography>
          </Stack>
          <TileGrid tiles={group.tiles} interactive={interactive} />
        </Box>
      ))}
    </Stack>
  );
};

const HomeScreen = () => {
  const [homeScreen, setHomeScreen] = useState(null);
  const [selectable, setSelectable] = useState([]);
  const [selectedId, setSelectedId] = useState('');

  const loadHomeScreen = () =>
    homeScreenService.getResolved().then((data) => {
      setHomeScreen(data ?? null);
      setSelectedId(data?.id ?? '');
    });

  useEffect(() => {
    Promise.all([loadHomeScreen(), homeScreenService.getSelectable().then((data) => setSelectable(data ?? []))]);
  }, []);

  const saveSelection = () => {
    homeScreenService.setMyAssignment(Number(selectedId)).then(() => {
      addNotification({ content: 'A kezdőképernyő kiválasztása sikeresen mentve.', type: NotificationType.SUCCESS });
      loadHomeScreen();
    });
  };

  const resetSelection = () => {
    homeScreenService.removeMyAssignment().then(() => {
      addNotification({ content: 'Visszaállt a jogosultsági csoport alapján beállított kezdőképernyő.', type: NotificationType.SUCCESS });
      loadHomeScreen();
    });
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: 'calc(100vh - 4rem)',
        bgcolor: '#f3f4f6',
        p: 2,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        ...getHomeScreenBackgroundStyle(homeScreen),
      }}
    >
      {selectable.length > 0 && (
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          gap={1}
          sx={{ width: 'fit-content', maxWidth: '100%', ml: 'auto', mb: 2.5, borderRadius: 3, border: '1px solid rgba(226,232,240,0.7)', bgcolor: 'rgba(255,255,255,0.9)', p: 1, boxShadow: 1 }}
        >
          <TextField
            select
            size="small"
            label="Kezdőképernyő"
            value={selectedId}
            onChange={(event) => setSelectedId(event.target.value)}
            sx={{ minWidth: 240 }}
          >
            {selectable.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={saveSelection} disabled={!selectedId}>
            Kiválasztás
          </Button>
          <Button variant="outlined" onClick={resetSelection}>
            Saját választás törlése
          </Button>
        </Stack>
      )}

      {homeScreen && <HomeScreenPreview homeScreen={homeScreen} interactive />}
    </Box>
  );
};

export default HomeScreen;
